// src/components/Character.tsx
import React from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { isAlienTemplate } from '../../../gameSystems/registry';

import EditableElement from '../EditableElement';
import type { CounterValue, EditableElementType } from '../EditableElement';
import { characterState, diceRollState } from '../../../recoil/UserInfo/atoms';
import {
  customDiceSelectedState,
  dicesBonusListState,
  diceRollMetaState,
  alienRollContextState
} from '../../../recoil/GameSystem/gameSystemAtom';
import {
  updateCharacter,
} from './../../../../common/services/characterRequests';
import type { SelectedDie } from '../../../recoil/GameSystem/gameSystemAtom';

//
// 1) Data shapes
//
interface CharacterElement {
  key?: string;
  label: string;
  role?: string;
  systemMetadata?: {
    linkedAttributeKey?: string;
  };
  value: string | number | CounterValue;
}

interface CharacterSection {
  name: string;
  type: EditableElementType;
  elements: CharacterElement[];
}

export interface CharacterType {
  ID?: string;
  name: string;
  template: {
    name: string;
    version: string;
    gameSystem?: string;
  };
  sections: CharacterSection[];
}

interface CharacterProps {
  character: CharacterType;
}

interface AlienRollDraft {
  label: string;
  baseDice: number;
  stressDice: number;
}

const ALIEN_RESOURCE_KEYS = new Set([
  'nivel_de_tension',
  'vitalidad',
  'radiacion',
  'puntos_de_experiencia',
  'puntos_de_trama',
  'aire',
  'comida',
  'energia',
  'agua',
]);

const ALIEN_RESOURCE_LABELS = new Set([
  'nivel de tensión',
  'nivel de tension',
  'vitalidad',
  'radiación',
  'radiacion',
  'puntos de experiencia',
  'puntos de trama',
  'aire',
  'comida',
  'energía',
  'energia',
  'agua',
]);

const Character: React.FC<CharacterProps> = ({ character }) => {
  const { t } = useTranslation();
  const [localCharacter, setLocalCharacter] = React.useState(character);
  const [, setSharedCharacter] = useRecoilState<any>(characterState);
  const [, setRoll] = useRecoilState(diceRollState);
  const [, setCustomDiceSelected] = useRecoilState(customDiceSelectedState);
  const [, setDicesBonusList] = useRecoilState(dicesBonusListState);
  const [, setDiceRollMeta] = useRecoilState(diceRollMetaState);
  const [, setAlienRollContext] = useRecoilState(alienRollContextState);
  const [alienRollDraft, setAlienRollDraft] = React.useState<AlienRollDraft | null>(null);
  const [alienModifier, setAlienModifier] = React.useState(0);
  const [alienWeaponBonus, setAlienWeaponBonus] = React.useState(0);

  React.useEffect(() => {
    setLocalCharacter(character);
    setSharedCharacter(character);
  }, [character, setSharedCharacter]);

  const handleKultRoll = (elementValue: number, label: string) => {
    setCustomDiceSelected('Kult');
    setDicesBonusList([
      { name: 'modifier', operation: '+', value: elementValue },
      { name: 'difficulty', operation: '>=', value: 15, errorRange: 5 },
    ]);
    setDiceRollMeta({ label, value: elementValue });
    setRoll([10, 10]);
  };

  const isAlienCharacter = isAlienTemplate(localCharacter.template);

  const getNumericValue = (value: string | number | CounterValue | undefined) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    if (value && typeof value === 'object') {
      return value.value;
    }
    return 0;
  };

  const findElement = (options: { key?: string; labels?: string[] }) => {
    const normalizedLabels = (options.labels || []).map((label) => label.toLowerCase());
    return localCharacter.sections
      .flatMap((section) => section.elements)
      .find((element) => {
        if (options.key && element.key === options.key) {
          return true;
        }

        return normalizedLabels.includes(element.label?.toLowerCase?.() || '');
      });
  };

  const buildAlienRollDice = (baseDice: number, stressDice: number): SelectedDie[] => {
    const base = Array.from({ length: baseDice }, () => ({
      notation: 6,
      color: 'black',
      label: 'base',
    }));
    const stress = Array.from({ length: stressDice }, () => ({
      notation: 6,
      color: 'yellow',
      label: 'stress',
    }));
    return [...base, ...stress];
  };

  const getAlienSectionAttribute = (section: CharacterSection) =>
    section.elements.find((candidate) => candidate.role === 'attribute') || section.elements[0];

  const isAlienResource = (element: CharacterElement) =>
    element.role === 'resource' ||
    ALIEN_RESOURCE_KEYS.has(element.key || '') ||
    ALIEN_RESOURCE_LABELS.has((element.label || '').toLowerCase());

  const handleAlienRoll = (element: CharacterElement, section: CharacterSection) => {
    const sectionAttribute = getAlienSectionAttribute(section);
    const behavesAsSkill =
      element.role === 'skill' ||
      Boolean(element.systemMetadata?.linkedAttributeKey) ||
      (section.type === 'number' &&
        sectionAttribute &&
        section.elements.indexOf(element) > 0 &&
        sectionAttribute !== element);
    const behavesAsAttribute =
      !behavesAsSkill &&
      (element.role === 'attribute' ||
        sectionAttribute === element ||
        section.type === 'number');
    const stressElement = findElement({
      key: 'nivel_de_tension',
      labels: ['Nivel de tensión', 'Nivel de tension'],
    });

    const attributeValue =
      behavesAsAttribute
        ? getNumericValue(element.value)
        : getNumericValue(sectionAttribute?.value);
    const skillValue = behavesAsSkill ? getNumericValue(element.value) : 0;
    const stressDice = getNumericValue(stressElement?.value);
    const baseDice = Math.max(0, attributeValue + skillValue);

    const rollLabel =
      behavesAsSkill && sectionAttribute
        ? `${sectionAttribute.label} + ${element.label}`
        : element.label;

    setAlienModifier(0);
    setAlienWeaponBonus(0);
    setAlienRollDraft({
      label: rollLabel,
      baseDice,
      stressDice,
    });
  };

  const closeAlienRollDialog = () => {
    setAlienRollDraft(null);
    setAlienModifier(0);
    setAlienWeaponBonus(0);
  };

  const confirmAlienRoll = () => {
    if (!alienRollDraft) {
      return;
    }

    const finalBaseDice = Math.max(
      0,
      alienRollDraft.baseDice + alienModifier + alienWeaponBonus
    );

    setCustomDiceSelected('Alien');
    setDicesBonusList([
      { name: 'base_dice', operation: '+', value: finalBaseDice },
      { name: 'stress_dice', operation: '+', value: alienRollDraft.stressDice },
    ]);
    setAlienRollContext({
      mode: 'skill',
      stressLevel: alienRollDraft.stressDice,
      sourceLabel: alienRollDraft.label,
    });
    setDiceRollMeta({ label: alienRollDraft.label, value: finalBaseDice });
    setRoll(buildAlienRollDice(finalBaseDice, alienRollDraft.stressDice));
    closeAlienRollDialog();
  };

  const isAlienRollable = (section: CharacterSection, element: CharacterElement) => {
    if (isAlienResource(element)) {
      return false;
    }

    if (element.role === 'skill' || element.role === 'attribute') {
      return true;
    }

    const sectionAttribute = getAlienSectionAttribute(section);
    const sectionLooksLikeAlienStats = section.elements.some(
      (candidate) => candidate.role === 'skill' || candidate.role === 'attribute'
    );
    const sectionContainsResources = section.elements.some((candidate) => isAlienResource(candidate));

    if (
      section.type === 'number' &&
      !sectionContainsResources &&
      !sectionLooksLikeAlienStats &&
      section.elements.length > 0
    ) {
      return true;
    }

    return section.type === 'number' && sectionLooksLikeAlienStats && Boolean(sectionAttribute);
  };

  const handleElementRoll = (section: CharacterSection, element: CharacterElement) => {
    if (isAlienCharacter) {
      if (isAlienRollable(section, element)) {
        handleAlienRoll(element, section);
      }
      return;
    }

    if (section.type !== 'counter') {
      handleKultRoll(getNumericValue(element.value), element.label);
    }
  };

  const isElementRollable = (section: CharacterSection, element: CharacterElement) => {
    if (isAlienCharacter) {
      return isAlienRollable(section, element);
    }
    return section.type !== 'counter';
  };

  const handleSave = async (updatedCharacter: CharacterType) => {
    if (!updatedCharacter.ID) return;
    try {
      await updateCharacter(updatedCharacter.ID, updatedCharacter);
      console.log('Character template saved successfully');
    } catch (error) {
      console.error('Error saving character template:', error);
    }
  };

  const handleElementChange = (
    sectionIndex: number,
    elementIndex: number,
    newValue: string | CounterValue
  ) => {
		const updatedCharacter = { ...localCharacter };
		const updatedSections = [...updatedCharacter.sections];
		const updatedElements = [...updatedSections[sectionIndex].elements];

		updatedElements[elementIndex] = {
			...updatedElements[elementIndex],
			value: newValue,
		};

		updatedSections[sectionIndex] = {
			...updatedSections[sectionIndex],
			elements: updatedElements,
		};

		updatedCharacter.sections = updatedSections;
		setLocalCharacter(updatedCharacter);
		setSharedCharacter(updatedCharacter);
		handleSave(updatedCharacter)
	};

  return (
    <Box>
      <Box p={2}>
        <Typography variant="h4">{localCharacter.name}</Typography>
        <Typography variant="subtitle1">
          Game System: {localCharacter.template.name}
        </Typography>
        <Typography variant="subtitle1">
          Version: {localCharacter.template.version}
        </Typography>
      </Box>

      {localCharacter.sections.map((section, sectionIndex) => (
        <Accordion key={sectionIndex} defaultExpanded sx={{ width: 700 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{section.name}</Typography>
          </AccordionSummary>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', p: 2 }}>
            {section.elements.map((el, elementIndex) => (
              <Box
                key={elementIndex}
                sx={{
                  mb: 2,
                  mr: 2,
                  p: 1,
                  cursor: isElementRollable(section, el)
                    ? 'pointer'
                    : isAlienResource(el)
                      ? 'text'
                      : 'default',
                  border: '1px dashed #ccc',
                }}
              >
                <EditableElement
                  name={el.label}
                  type={section.type}
                  value={el.value}
                  editable={isAlienResource(el)}
                  roll={() => handleElementRoll(section, el)}
                  onChange={(newValue) =>
										handleElementChange(sectionIndex, elementIndex, newValue)
									}
                />
              </Box>
            ))}
          </Box>
        </Accordion>
      ))}
      <Dialog open={Boolean(alienRollDraft)} onClose={closeAlienRollDialog} fullWidth maxWidth="xs">
        <DialogTitle>{t('diceResults.alien.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {alienRollDraft?.label}
          </Typography>
          <TextField
            fullWidth
            margin="dense"
            label={t('diceResults.alien.baseDice')}
            value={alienRollDraft?.baseDice ?? 0}
            InputProps={{ readOnly: true }}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('diceResults.alien.stressDice')}
            value={alienRollDraft?.stressDice ?? 0}
            InputProps={{ readOnly: true }}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('diceResults.alien.situationalModifier')}
            type="number"
            value={alienModifier}
            onChange={(event) => setAlienModifier(parseInt(event.target.value || '0', 10) || 0)}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('diceResults.alien.weaponBonus')}
            type="number"
            value={alienWeaponBonus}
            onChange={(event) => setAlienWeaponBonus(parseInt(event.target.value || '0', 10) || 0)}
          />
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('diceResults.alien.finalBaseDice', {
              value: Math.max(0, (alienRollDraft?.baseDice ?? 0) + alienModifier + alienWeaponBonus),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAlienRollDialog}>{t('diceResults.alien.cancel')}</Button>
          <Button variant="contained" onClick={confirmAlienRoll}>{t('diceResults.alien.roll')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Character;
